/**
 * Utilization calculation functions
 *
 * These functions calculate utilization metrics and assess capacity risk levels.
 * Uses the existing kpisHelper for consistency with other parts of the application.
 */

import { calculateProviderUtilization } from '@caredna-marlin/utils/kpisHelper';

/**
 * Calculate utilization percentage for a provider on a given day
 * @param shiftMinutes - Total shift time in minutes
 * @param visitMinutes - Total visit time in minutes
 * @param breakMinutes - Total break time in minutes
 * @returns Utilization percentage (0-100+)
 */
export function calculateUtilization(
  shiftMinutes: number,
  visitMinutes: number,
  breakMinutes: number
): number {
  // Reuse existing utility function for consistency
  return calculateProviderUtilization({
    shiftDuration: shiftMinutes,
    visitResourcesDuration: visitMinutes,
    breaksDuration: breakMinutes,
  });
}

/**
 * Calculate remaining capacity in minutes
 * @param shiftMinutes - Total shift time in minutes
 * @param visitMinutes - Total visit time in minutes
 * @param breakMinutes - Total break time in minutes
 * @returns Remaining minutes available
 */
export function calculateRemainingMinutes(
  shiftMinutes: number,
  visitMinutes: number,
  breakMinutes: number
): number {
  const effectiveShiftTime = shiftMinutes - breakMinutes;
  return Math.max(0, effectiveShiftTime - visitMinutes);
}

/**
 * Check if provider is over capacity
 * @param visitMinutes - Total visit time in minutes
 * @param shiftMinutes - Total shift time in minutes
 * @param breakMinutes - Total break time in minutes
 * @returns True if over capacity
 */
export function isOverCapacity(
  visitMinutes: number,
  shiftMinutes: number,
  breakMinutes: number
): boolean {
  const effectiveShiftTime = shiftMinutes - breakMinutes;
  return visitMinutes > effectiveShiftTime;
}

/**
 * Assess risk level based on utilization and remaining capacity
 * @param utilization - Utilization percentage
 * @param remainingMinutes - Remaining capacity in minutes
 * @returns Risk level: 'low', 'medium', or 'high'
 */
export function assessRiskLevel(
  utilization: number,
  remainingMinutes: number
): 'low' | 'medium' | 'high' {
  // High risk: >= 95% utilization or < 30 minutes remaining
  if (utilization >= 95 || remainingMinutes < 30) {
    return 'high';
  }

  // Medium risk: >= 85% utilization or < 120 minutes remaining
  if (utilization >= 85 || remainingMinutes < 120) {
    return 'medium';
  }

  // Low risk: everything else
  return 'low';
}
